import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Plus, Minus, X, UserPlus, Loader2, Info, XCircle, Save } from "lucide-react";
import { fetchOrganizations, fetchRoles } from "../apiHelper"; // Assuming these accept user object if needed
import FastAPIClient from "../services/apiClient";
import { useToast } from "../hooks/useToast";
import Input from "../components/ui/Input";
import Button from "../components/ui/Button";
import Card from "../components/ui/Card";
import Select from 'react-select'
// import SearchableSelectBox from "../components/ui/SearchableSelectBox"; // Using SearchableSelectBox
import { useNavigate } from "react-router-dom"; // Import useNavigate

// Initialize the API client
const client = new FastAPIClient();

/**
 * NewStaffMember Component.
 * Allows an administrator to invite multiple staff members by providing their details
 * and assigning them to organizations and roles.
 * Features: Dynamic form rows, client-side validation, API integration,
 * and display of failed invitations.
 * Designed with Tailwind CSS and integrated with custom UI components.
 */
const NewStaffMember = () => {
    const navigate = useNavigate();
    const { showToast } = useToast(); // Use the custom useToast hook

    const [loggedInUser, setLoggedInUser] = useState(null);
    const [rolesOptions, setRolesOptions] = useState([]);
    const [orgsOptions, setOrgsOptions] = useState([]);
    const [loadingInitialData, setLoadingInitialData] = useState(true);
    const [initialDataError, setInitialDataError] = useState(null);

    const initialRowState = useMemo(() => ({
        first_name: '',
        last_name: '',
        email: '',
        referer_id: null, // Holds the { label, value } object for organization
        employeeid: '',
        roles: [], // Holds array of { label, value } objects for roles
    }), []);

    const [rows, setRows] = useState([initialRowState]);
    const [rowErrors, setRowErrors] = useState({}); // Stores errors per row: { rowIndex: { fieldName: 'error message' } }
    const [submitStatus, setSubmitStatus] = useState('idle'); // 'idle', 'loading', 'success', 'error'
    const [failedInvites, setFailedInvites] = useState([]);
    const failedTableRef = useRef(null);

    // Effect to load user from local storage and fetch initial data
    useEffect(() => {
        const loadInitialData = async () => {
            setLoadingInitialData(true);
            setInitialDataError(null);
            const lcUser = localStorage.getItem("user");

            if (!lcUser) {
                showToast("Please log in to invite staff.", "info");
                setLoadingInitialData(false);
                navigate('/login'); // Optionally redirect to login
                return;
            }

            const parsedUser = JSON.parse(lcUser);
            setLoggedInUser(parsedUser);

            try {
                // Fetch roles
                const fetchedRoles = await fetchRoles(); // Assuming fetchRoles does not need user_id or handles it internally
                const mappedRoles = fetchedRoles.map(role => ({
                    label: role.name,
                    value: role.role_id,
                }));
                setRolesOptions(mappedRoles);

                // Fetch organizations
                const fetchedOrganizations = await fetchOrganizations(); // Assuming fetchOrganizations does not need user_id or handles it internally
                const mappedOrgs = fetchedOrganizations.map(org => ({
                    label: org.organization_name,
                    value: org.organization_id,
                }));
                setOrgsOptions(mappedOrgs);

            } catch (err) {
                console.error('Error loading initial data:', err);
                setInitialDataError(err.response?.data?.detail || "Failed to load initial data (roles/organizations).");
                showToast(err.response?.data?.detail || "Failed to load roles or organizations.", 'error');
            } finally {
                setLoadingInitialData(false);
            }
        };

        loadInitialData();
    }, [showToast]);

    // Update individual row field
    const updateRow = useCallback((index, key, value) => {
        setRows(prevRows => {
            const newRows = [...prevRows];
            newRows[index] = { ...newRows[index], [key]: value };
            return newRows;
        });
        // Clear specific field error as user types
        setRowErrors(prevErrors => {
            const newRowErrors = { ...prevErrors[index], [key]: '' };
            if (Object.values(newRowErrors).every(err => !err)) {
                const updatedErrors = { ...prevErrors };
                delete updatedErrors[index];
                return updatedErrors;
            }
            return { ...prevErrors, [index]: newRowErrors };
        });
    }, []);

    // Add a new empty row
    const addRow = useCallback(() => {
        setRows(prevRows => [...prevRows, { ...initialRowState }]);
    }, [initialRowState]);

    // Remove a row
    const removeRow = useCallback((index) => {
        setRows(prevRows => prevRows.filter((_, i) => i !== index));
        setRowErrors(prevErrors => {
            const newErrors = { ...prevErrors };
            delete newErrors[index];
            return newErrors;
        });
    }, []);

    // Client-side validation for all rows
    const validateForm = useCallback(() => {
        let newErrors = {};
        let isValid = true;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (rows.length === 0) {
            showToast("At least one staff member entry is required.", 'error');
            return false;
        }

        rows.forEach((row, index) => {
            let currentRowErrors = {};
            if (!row.first_name.trim()) {
                currentRowErrors.first_name = 'First Name is required.';
                isValid = false;
            }
            if (!row.last_name.trim()) {
                currentRowErrors.last_name = 'Last Name is required.';
                isValid = false;
            }
            if (!row.email.trim()) {
                currentRowErrors.email = 'Email is required.';
                isValid = false;
            } else if (!emailRegex.test(row.email)) {
                currentRowErrors.email = 'Invalid email format.';
                isValid = false;
            }
            if (!row.referer_id) {
                currentRowErrors.referer_id = 'Organization is required.';
                isValid = false;
            }
            if (row.roles.length === 0) {
                currentRowErrors.roles = 'At least one role is required.';
                isValid = false;
            }

            if (Object.keys(currentRowErrors).length > 0) {
                newErrors[index] = currentRowErrors;
            }
        });

        setRowErrors(newErrors);
        if (!isValid) {
            showToast("Please correct the errors in the form.", 'error');
        }
        return isValid;
    }, [rows, showToast]);

    // Handle form submission
    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        setSubmitStatus('loading');
        setFailedInvites([]); // Clear previous failed invites

        const payload = rows.map(r => ({
            email: r.email,
            first_name: r.first_name,
            last_name: r.last_name,
            referer_id: r.referer_id?.value, // Send only the value (ID)
            employeeid: r.employeeid.trim() || null, // Send null if empty
            roles: r.roles.map(role => role.value) // Send array of role IDs
        }));

        // console.log('Submitting staff invite payload:', payload);

        try {
            const response = await client.postData('/auth/staff-invite', payload);
            console.log('Mass invite response:', response);

            const failed = response?.results?.filter(r => r.status === 'failed') || [];
            setFailedInvites(failed);

            if (failed.length > 0) {
                showToast(`Mass invite completed with ${failed.length} failed invitations.`, 'warning');
                // Scroll to failed invites table
                if (failedTableRef.current) {
                    setTimeout(() => failedTableRef.current.scrollIntoView({ behavior: 'smooth' }), 100);
                }
            } else {
                showToast("All staff invited successfully!", 'success');
                setRows([initialRowState]); // Reset form to initial state on full success
            }
            setSubmitStatus('success');
        } catch (err) {
            console.error('Failed to mass-invite users:', err);
            const errorMessage = err.response?.data?.detail || `Failed to invite staff: ${err.message || err}`;
            showToast(errorMessage, 'error');
            setSubmitStatus('error');
            // If backend returns failed_rows on error, capture them
            if (err.response?.data?.failed_rows) {
                setFailedInvites(err.response.data.failed_rows);
            }
        } finally {
            setSubmitStatus('idle'); // Reset status regardless of success/failure
        }
    };

    if (loadingInitialData) {
        return (
            <div className="flex items-center justify-center min-h-[60vh] text-center p-8 text-text-secondary">
                <Loader2 size={32} className="animate-spin text-gmail-blue" />
                <p className="ml-3 text-lg">Loading roles and organizations...</p>
            </div>
        );
    }

    if (initialDataError) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-8 text-gmail-red">
                <XCircle size={32} className="mb-2" />
                <p className="text-lg mb-4">Error: {initialDataError}</p>
                <Button onClick={() => window.location.reload()} variant="primary">
                    Reload Page
                </Button>
            </div>
        );
    }

    if (!loggedInUser) {
        return (
            <div className='flex flex-col items-center justify-center min-h-[60vh] text-center p-8 text-orange-600'>
                <Info size={32} className="mb-2" />
                <p className="text-lg mb-4">Please log in to invite staff.</p>
                <Button onClick={() => navigate('/login')} variant="primary">
                    Go to Login
                </Button>
            </div>
        );
    }

    // Check if essential options are available
    if (rolesOptions.length === 0 || orgsOptions.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-4">
                <p className="text-gmail-red text-lg mb-4">
                    Cannot invite staff: No roles or organizations found. Please ensure these are created first.
                </p>
                <div className="flex gap-4 mt-4">
                    {rolesOptions.length === 0 && (
                        <Button onClick={() => navigate('/roles/create')} variant="secondary">
                            Create Roles
                        </Button>
                    )}
                    {orgsOptions.length === 0 && (
                        <Button onClick={() => navigate('/organizations/create')} variant="secondary">
                            Create Organizations
                        </Button>
                    )}
                </div>
            </div>
        );
    }


    return (
        <div className="container mx-auto px-4 py-6">
            <Card className="p-6">
                <h2 className="text-2xl font-bold text-center text-text-primary mb-6 flex items-center justify-center gap-2">
                    <UserPlus size={28} className="text-gmail-blue" /> Invite New Staff Members
                </h2>
                <p className="text-center text-text-secondary mb-8 text-sm">
                    Add multiple staff members at once by filling in their details, assigning an organization, and selecting roles.
                </p>

                <div className="overflow-x-auto custom-scrollbar pb-4">
                    <table className="min-w-full table-auto text-sm border-collapse">
                        <thead className="text-left bg-gray-50 border-b border-gray-200">
                            <tr>
                                <th className="px-4 py-3 font-semibold text-text-secondary uppercase tracking-wider">First Name <span className="text-gmail-red">*</span></th>
                                <th className="px-4 py-3 font-semibold text-text-secondary uppercase tracking-wider">Last Name <span className="text-gmail-red">*</span></th>
                                <th className="px-4 py-3 font-semibold text-text-secondary uppercase tracking-wider">Email <span className="text-gmail-red">*</span></th>
                                <th className="px-4 py-3 font-semibold text-text-secondary uppercase tracking-wider">Organization <span className="text-gmail-red">*</span></th>
                                <th className="px-4 py-3 font-semibold text-text-secondary uppercase tracking-wider">Staff ID</th>
                                <th className="px-4 py-3 font-semibold text-text-secondary uppercase tracking-wider">Roles <span className="text-gmail-red">*</span></th>
                                <th className="px-4 py-3 font-semibold text-text-secondary uppercase tracking-wider text-center">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {rows.map((row, i) => (
                                <tr key={i} className="hover:bg-gray-50 transition-colors duration-150">
                                    <td className="px-4 py-2">
                                        <Input
                                            type="text"
                                            value={row.first_name}
                                            placeholder="First Name"
                                            onChange={(e) => updateRow(i, 'first_name', e.target.value)}
                                            className="!py-1 !px-2 !text-sm"
                                            containerClassName="!mb-0"
                                            error={rowErrors[i]?.first_name}
                                        />
                                        {rowErrors[i]?.first_name && <p className="text-gmail-red text-xs mt-1">{rowErrors[i].first_name}</p>}
                                    </td>
                                    <td className="px-4 py-2">
                                        <Input
                                            type="text"
                                            value={row.last_name}
                                            placeholder="Last Name"
                                            onChange={(e) => updateRow(i, 'last_name', e.target.value)}
                                            className="!py-1 !px-2 !text-sm"
                                            containerClassName="!mb-0"
                                            error={rowErrors[i]?.last_name}
                                        />
                                        {rowErrors[i]?.last_name && <p className="text-gmail-red text-xs mt-1">{rowErrors[i].last_name}</p>}
                                    </td>
                                    <td className="px-4 py-2">
                                        <Input
                                            type="email"
                                            value={row.email}
                                            placeholder="Email Address"
                                            onChange={(e) => updateRow(i, 'email', e.target.value)}
                                            className="!py-1 !px-2 !text-sm"
                                            containerClassName="!mb-0"
                                            error={rowErrors[i]?.email}
                                        />
                                        {rowErrors[i]?.email && <p className="text-gmail-red text-xs mt-1">{rowErrors[i].email}</p>}
                                    </td>
                                    <td className="px-4 py-2">
                                        <Select className="text-sm"
                                            options={orgsOptions}
                                            value={row.referer_id}
                                            onChange={(option) => updateRow(i, 'referer_id', option)}
                                            isClearable
                                            placeholder="Employer" />
                                        {/* <SearchableSelectBox
                                            options={orgsOptions}
                                            value={row.referer_id} // This should be the { label, value } object
                                            onChange={(e) => { // 'e' is the event object from SearchableSelectBox
                                                const selectedOrgValue = e.target.value; // Extract the value (organization_id)
                                                // Find the full organization object from orgsOptions using the value
                                                const fullOrgObject = orgsOptions.find(opt => opt.value === selectedOrgValue);
                                                updateRow(i, 'referer_id', fullOrgObject); // Pass the full object {label, value}
                                            }}
                                            isClearable
                                            placeholder="Select Organization"
                                            className="!text-sm"
                                            containerClassName="!mb-0"
                                            error={rowErrors[i]?.referer_id}
                                        />
                                        {rowErrors[i]?.referer_id && <p className="text-gmail-red text-xs mt-1">{rowErrors[i].referer_id}</p>} */}
                                    </td>
                                    <td className="px-4 py-2">
                                        <Input
                                            type="text"
                                            value={row.employeeid}
                                            placeholder="Staff ID (Optional)"
                                            onChange={(e) => updateRow(i, 'employeeid', e.target.value)}
                                            className="!py-1 !px-2 !text-sm"
                                            containerClassName="!mb-0"
                                            error={rowErrors[i]?.employeeid}
                                        />
                                        {rowErrors[i]?.employeeid && <p className="text-gmail-red text-xs mt-1">{rowErrors[i].employeeid}</p>}
                                    </td>
                                    <td className="px-4 py-2">
                                        <div className="mb-1 flex flex-wrap gap-1">
                                            {row.roles.map(role => (
                                                <div 
                                                    key={role.value}
                                                    className="bg-gray-200 rounded-full px-3 py-1 text-xs text-gray-700 flex items-center">
                                                        {role.label}
                                                        <button 
                                                            onClick={() => 
                                                                updateRow(i, 'roles', row.roles.filter(r => r.value !== role.value))
                                                            }
                                                            className="ml-2 text-gray-500 hover:text-red-600">
                                                                <X size={12} />
                                                        </button>
                                                </div>
                                            ))}
                                        </div>
                                        <Select
                                            options={rolesOptions}
                                            value={null}
                                            onChange={(selected)=> {
                                                if (!row.roles.some(r => r.value === selected.value)) {
                                                    updateRow(i, 'roles', [...row.roles, selected])
                                                }
                                            }}
                                            isClearable
                                            placeholder="Roles"
                                            className="text-sm" />
                                        {/* <div className="mb-1 flex flex-wrap gap-1">
                                            {row.roles.map(role => (
                                                <div
                                                    key={role.value}
                                                    className="bg-gray-200 rounded-full px-3 py-1 text-xs text-gray-700 flex items-center"
                                                >
                                                    {role.label}
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            updateRow(i, 'roles', row.roles.filter(r => r.value !== role.value))
                                                        }
                                                        className="ml-2 text-gray-500 hover:text-gmail-red focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-gmail-red rounded-full"
                                                        aria-label={`Remove role ${role.label}`}
                                                    >
                                                        <X size={12} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                        <SearchableSelectBox
                                            options={rolesOptions}
                                            value={null} // Always null for single-select to add new roles to the array
                                            onChange={(e) => { // 'e' is the event object from SearchableSelectBox
                                                const selectedRoleValue = e.target.value; // Extract the value (role_id)
                                                // Find the full role object from rolesOptions using the value
                                                const fullRoleObject = rolesOptions.find(opt => opt.value === selectedRoleValue);

                                                if (fullRoleObject && !row.roles.some(r => r.value === fullRoleObject.value)) {
                                                    updateRow(i, 'roles', [...row.roles, fullRoleObject]);
                                                }
                                            }}
                                            isClearable
                                            placeholder="Add Roles"
                                            className="!text-sm"
                                            containerClassName="!mb-0"
                                            error={rowErrors[i]?.roles}
                                        />
                                        {rowErrors[i]?.roles && <p className="text-gmail-red text-xs mt-1">{rowErrors[i].roles}</p>} */}
                                    </td>
                                    <td className="px-2 text-center align-middle">
                                        {rows.length > 1 && (
                                            <Button
                                                type="button"
                                                onClick={() => removeRow(i)}
                                                variant="ghost"
                                                size="sm"
                                                className="text-gmail-red hover:bg-red-100 p-1.5 rounded-full"
                                                aria-label={`Remove row ${i + 1}`}
                                            >
                                                <Minus size={16} />
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="flex justify-between items-center mt-6">
                    <Button
                        type="button"
                        onClick={addRow}
                        variant="secondary"
                        className="flex items-center gap-2 px-4 py-2"
                    >
                        <Plus size={20} /> Add Row
                    </Button>
                    <Button
                        type="button"
                        onClick={handleSubmit}
                        variant="primary"
                        size="lg"
                        disabled={submitStatus === 'loading'}
                        className="flex items-center gap-2 px-6 py-3"
                    >
                        {submitStatus === 'loading' ? (
                            <>
                                <Loader2 className="animate-spin h-5 w-5" /> Submitting...
                            </>
                        ) : (
                            <>
                                <Save size={20} /> Invite Staff
                            </>
                        )}
                    </Button>
                </div>

                {failedInvites.length > 0 && (
                    <div ref={failedTableRef} className="mt-10 border-t border-gray-200 pt-6">
                        <h3 className="text-xl font-bold mb-4 text-gmail-red flex items-center gap-2">
                            <XCircle size={24} /> Failed Invitations
                        </h3>
                        <p className="text-sm text-text-secondary mb-4">
                            The following invitations failed. Please review the reasons and try again.
                        </p>
                        <div className="overflow-x-auto custom-scrollbar rounded-lg border border-red-200 bg-red-50">
                            <table className="min-w-full table-auto text-sm">
                                <thead className="text-left bg-red-100 border-b border-red-200">
                                    <tr>
                                        <th className="px-4 py-3 font-semibold text-red-800 uppercase tracking-wider">Email</th>
                                        <th className="px-4 py-3 font-semibold text-red-800 uppercase tracking-wider">Reason</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-red-200">
                                    {failedInvites.map((fail, idx) => (
                                        <tr key={idx} className="hover:bg-red-100 transition-colors duration-150">
                                            <td className="px-4 py-2 text-red-700 font-medium">{fail.email}</td>
                                            <td className="px-4 py-2 text-red-700">{fail.reason || "Unknown error"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </Card>
        </div>
    );
};

export default NewStaffMember;
